import {
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { Button, Input, message, Tooltip, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowStep } from '@/api/workflows';
import { notifySessionActivity } from '@/auth/sessionTimer';

import { evaluateConditions } from './evaluateConditions';
import type { FieldValue } from './FieldRenderer';
import { fileIcon } from './fileIcon';
import { downloadFileToDevice, openFileInNewTab } from './filePreviewActions';
import { formatFileSize } from './formatFileSize';
import type { UploadedBucketFile, UploadedEntryFile, UploadedFilesStore } from './uploadedFiles';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { TextArea } = Input;

// Fallback file-size cap for legacy rows that pre-date the per-entry +
// per-bucket caps. Anything live carries its own `maxFileSizeMb` from the
// BE; this only kicks in if a row somehow arrives without one.
const DEFAULT_MAX_FILE_SIZE_MB = 2;

// AC4 (INFLOWS-201) — the file types the browser can render inline in a new
// tab. Anything not in this set falls through to "download only" and the
// preview button is disabled with a tooltip explaining why. Deliberately
// short: Office / text formats need a viewer library or a server round-trip
// to preview reliably, and both are out of scope for an upload-step affordance.
const INLINE_PREVIEWABLE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

export function canPreviewInBrowser(name: string): boolean {
  return INLINE_PREVIEWABLE_EXTENSIONS.includes(extensionOf(name));
}

/** Shared per-file action strip — Preview + Download + Remove — reused by
 *  both the named-entry slots and the catch-all bucket rows. Isolated as a
 *  component so the row markup stays identical across the two contexts. */
function FileRowActions({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useTranslation();
  const previewable = canPreviewInBrowser(file.name);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <Tooltip
        title={
          previewable
            ? t('formRenderer.additionalDocuments.preview')
            : t('formRenderer.additionalDocuments.previewNotAvailable')
        }
      >
        {/* Use aria-disabled instead of `disabled` so keyboard users can tab
            onto the button and the Tooltip fires on focus — a truly disabled
            button is removed from tab order and the "preview not available"
            hint would be unreachable for screen-reader / keyboard-only
            advisors. The click handler no-ops when previewable is false so
            the visual "unavailable" state still holds. */}
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-disabled={!previewable}
          onClick={() => {
            if (!previewable) return;
            openFileInNewTab(file, t);
          }}
          aria-label={t('formRenderer.additionalDocuments.preview')}
          style={previewable ? undefined : { opacity: 0.4, cursor: 'not-allowed' }}
        />
      </Tooltip>
      <Tooltip title={t('formRenderer.additionalDocuments.download')}>
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => downloadFileToDevice(file)}
          aria-label={t('formRenderer.additionalDocuments.download')}
        />
      </Tooltip>
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined />}
        onClick={onRemove}
        aria-label={t('formRenderer.additionalDocuments.remove')}
      />
    </div>
  );
}

/**
 * Advisor-side runtime UI for an `AdditionalDocuments` step. Renders one
 * upload slot per originator-configured entry (mirrors `PdfGenerationStep`'s
 * one-row-per-entry list), each enforcing its own allowed extensions and
 * showing whether it's required. Pure frontend — picked files are tracked in
 * local component state only (no upload to the BE yet); backend persistence
 * + S3 staging land alongside the real Submit flow.
 */
export function AdditionalDocumentsStep({
  step,
  language,
  values,
  uploadedFiles,
  onUploadedFilesChange,
}: {
  step: WorkflowStep;
  language: 'en' | 'fr';
  /** Current advisor answers — drives entry-level visibility conditions so
   *  "Void Cheque only when Payment = EFT" hides the row when Payment is
   *  something else. Matches the pattern DynamicForm's section conditions
   *  use. Optional so preview/tests that don't own a values map still work
   *  (in that case every entry renders unconditionally). */
  values?: Record<string, FieldValue>;
  /** FormShell-owned store — survives step navigation and is consumed
   *  by the PdfGeneration step downstream. See `uploadedFiles.ts`. */
  uploadedFiles: UploadedFilesStore;
  onUploadedFilesChange: (
    updater: UploadedFilesStore | ((prev: UploadedFilesStore) => UploadedFilesStore),
  ) => void;
}) {
  const { t } = useTranslation();
  const setEntryFiles = (entryId: string, files: UploadedEntryFile[]): void => {
    // Counts as session activity: an advisor actively uploading files shouldn't hit the
    // idle-refresh boundary mid-upload. See useSessionTimer.ts.
    notifySessionActivity();
    onUploadedFilesChange((prev) => ({
      ...prev,
      entries: { ...prev.entries, [entryId]: files },
    }));
  };
  const setBucket = (files: UploadedBucketFile[]): void => {
    notifySessionActivity();
    onUploadedFilesChange((prev) => ({ ...prev, bucket: files }));
  };

  // Entry-level visibility conditions — mirror the DynamicForm section
  // pattern so an entry with unmet conditions doesn't render (and doesn't
  // count against Next-gating either; see the matching branch in
  // FormShell's `isStepComplete` / `missingRequiredFields`).
  const allEntries = step.additionalDocumentEntries ?? [];
  const entries = allEntries.filter((entry) => {
    if ((entry.conditions?.length ?? 0) === 0) return true;
    return evaluateConditions(entry.conditions, entry.conditionCombinator, values ?? {});
  });
  const bucketExtensions = step.optionalDocumentExtensions ?? [];
  // Advisor/preview visibility toggle set by the originator — same component
  // renders both preview and the live advisor form, so this one guard covers
  // both. See AdditionalDocumentEntriesDrawer's "Other Additional Documents"
  // Switch.
  const bucketVisible = bucketExtensions.length > 0 && step.showOptionalDocumentsSection;
  const description = language === 'fr' ? step.descriptionFr : step.descriptionEn;

  if (entries.length === 0 && !bucketVisible) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {description && (
          <Paragraph style={{ marginBottom: 0, color: '#111827', fontSize: 14, fontWeight: 600 }}>
            {description}
          </Paragraph>
        )}
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('formRenderer.additionalDocuments.empty')}
        </Text>
      </div>
    );
  }

  // Combined caps quoted under each group heading, per the 2026-07-27 BMO
  // design. Entries can each carry their own cap, so the group figure is the
  // largest configured — quoting a single number that some file could exceed
  // would be worse than quoting the ceiling.
  const mandatoryCapMb = entries.reduce(
    (max, e) => Math.max(max, e.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB),
    0,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div>
        {/* The step title renders only in the left rail, so the content area
            needs its own page heading — same rule as the DynamicForm steps. */}
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 8px 0' }}>
          {(language === 'fr' ? step.titleFr : step.titleEn) ||
            t('formRenderer.additionalDocuments.pageTitle')}
        </h2>
        {description && (
          <Paragraph style={{ marginBottom: 0, color: '#475569', fontSize: 14 }}>
            {description}
          </Paragraph>
        )}
      </div>

      {entries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <GroupHeading
            title={t('formRenderer.additionalDocuments.mandatoryHeading')}
            capMb={mandatoryCapMb}
            t={t}
          />
          {entries.map((entry) => (
            <EntrySlot
              key={entry.id}
              label={entry.label}
              isRequired={entry.isRequired}
              allowedExtensions={entry.allowedExtensions}
              maxFileSizeMb={entry.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB}
              files={uploadedFiles.entries[entry.id] ?? []}
              onFilesChange={(files) => setEntryFiles(entry.id, files)}
            />
          ))}
        </div>
      )}

      {bucketVisible && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <GroupHeading
            title={t('formRenderer.additionalDocuments.optionalDocsHeading')}
            capMb={step.optionalDocumentMaxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB}
            t={t}
          />
          <OptionalDocumentsBucket
            allowedExtensions={bucketExtensions}
            maxFileSizeMb={step.optionalDocumentMaxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB}
            files={uploadedFiles.bucket}
            onFilesChange={setBucket}
          />
        </div>
      )}
    </div>
  );
}

/** "Mandatory Documents" + the combined-size line beneath it. */
function GroupHeading({
  title,
  capMb,
  t,
}: {
  title: string;
  capMb: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px 0' }}>{title}</h2>
      <Text style={{ fontSize: 14, color: '#475569' }}>
        {t('formRenderer.additionalDocuments.combinedSize')} <strong>{capMb} MB</strong>.
      </Text>
    </div>
  );
}

/**
 * The dashed drop target. One line — "Browse files or drag and drop from your
 * device" — with only "Browse files" in BMO blue, per the client's Figma. The
 * old treatment stacked a cloud glyph, a prompt and an accepted-formats line;
 * formats now sit beside the label on the left instead.
 */
function DropZone({
  beforeUpload,
  acceptedAttr,
  t,
}: {
  beforeUpload: (file: RcFile) => boolean | string;
  acceptedAttr: string | undefined;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <Dragger
      multiple
      showUploadList={false}
      beforeUpload={beforeUpload}
      accept={acceptedAttr}
      className="bmo-dropzone"
    >
      <span style={{ fontSize: 14, color: '#0179C1', fontWeight: 700 }}>
        {t('formRenderer.additionalDocuments.browseFiles')}
      </span>{' '}
      <span style={{ fontSize: 14, color: '#111827' }}>
        {t('formRenderer.additionalDocuments.orDragDrop')}
      </span>
    </Dragger>
  );
}

/** Extensions that name the SAME format — collapsed for display so a slot
 *  accepting .jpg and .jpeg reads "JPEG", not "JPG, JPEG". Validation still
 *  uses the raw extension list; this is presentation only. */
const FORMAT_DISPLAY_NAME: Record<string, string> = { jpg: 'JPEG', jpeg: 'JPEG' };

/** "JPEG or PDF" / "JPEG, PNG or PDF" — the design joins with "or", not commas. */
function formatNames(allowedExtensions: string[]): string {
  const names = Array.from(
    new Set(allowedExtensions.map((e) => FORMAT_DISPLAY_NAME[e.toLowerCase()] ?? e.toUpperCase())),
  );
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/** One originator-configured document slot — its own dropzone, validation, and file list. */
function EntrySlot({
  label,
  isRequired,
  allowedExtensions,
  maxFileSizeMb,
  files,
  onFilesChange,
}: {
  label: string;
  isRequired: boolean;
  allowedExtensions: string[];
  maxFileSizeMb: number;
  files: UploadedEntryFile[];
  onFilesChange: (files: UploadedEntryFile[]) => void;
}) {
  const { t } = useTranslation();

  const acceptedAttr =
    allowedExtensions.length > 0 ? allowedExtensions.map((e) => `.${e}`).join(',') : undefined;
  const allowedListText = allowedExtensions.map((e) => e.toUpperCase()).join(', ');
  const maxBytes = maxFileSizeMb * 1024 * 1024;

  // AntD's beforeUpload accepts `boolean | string | Promise<...>`. We return
  // either `false` (track locally, don't auto-POST) OR the `LIST_IGNORE`
  // sentinel string (reject + don't put on AntD's internal list). The literal
  // string `Upload.LIST_IGNORE` is the contract; never cast it to `false`,
  // wrappers branching on the truthy/falsy of the return would mis-handle a
  // truthy "__LIST_IGNORE__" string.
  const beforeUpload = (file: RcFile): boolean | string => {
    if (allowedExtensions.length > 0) {
      const dot = file.name.lastIndexOf('.');
      const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
      if (!allowedExtensions.includes(ext)) {
        void message.error(
          t('formRenderer.additionalDocuments.rejectedExtension', {
            name: file.name,
            allowed: allowedListText,
          }),
        );
        return Upload.LIST_IGNORE;
      }
    }
    if (file.size > maxBytes) {
      void message.error(
        t('formRenderer.additionalDocuments.rejectedSize', {
          name: file.name,
          max: maxFileSizeMb,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    // Track locally so PdfGenerationStep can read the actual File later.
    // Append a monotonic suffix so re-picking the same file (identical
    // name + size + lastModified after a remove + re-add) still produces
    // a unique React key.
    onFilesChange([
      ...files,
      {
        uid: `${file.name}-${file.size}-${file.lastModified}-${files.length}`,
        file,
      },
    ]);
    return false;
  };

  const removeFile = (uid: string) => onFilesChange(files.filter((f) => f.uid !== uid));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Label + accepted formats on the left, drop target on the right —
          the Figma's two-column slot. The old "Required" / "Optional" tags
          are gone: a red asterisk carries the same meaning and matches every
          other required field in the form. */}
      <div className="bmo-doc-slot">
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span className="bmo-doc-slot__bullet" aria-hidden="true" />
            <Text strong style={{ fontSize: 14, color: '#111827' }}>
              {label}
              {isRequired && (
                <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>
                  *
                </span>
              )}
            </Text>
          </div>
          <div style={{ marginLeft: 16, marginTop: 2, fontSize: 12, color: '#6b7280' }}>
            {allowedExtensions.length > 0
              ? t('formRenderer.additionalDocuments.formatsOnly', {
                  list: formatNames(allowedExtensions),
                })
              : t('formRenderer.additionalDocuments.anyFormat')}
          </div>
        </div>
        <DropZone beforeUpload={beforeUpload} acceptedAttr={acceptedAttr} t={t} />
      </div>

      {files.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {files.map((f) => (
            <li
              key={f.uid}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#fff',
              }}
            >
              {fileIcon(f.file.name)}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {f.file.name}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{formatFileSize(f.file.size)}</div>
              </div>
              <FileRowActions file={f.file} onRemove={() => removeFile(f.uid)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The unnamed "optional documents" bucket — a single dropzone that takes any
 * number of files of the originator-configured types, with no per-file name.
 * The advisor may attach an optional free-text description to each file.
 */
function OptionalDocumentsBucket({
  allowedExtensions,
  maxFileSizeMb,
  files,
  onFilesChange,
}: {
  allowedExtensions: string[];
  maxFileSizeMb: number;
  files: UploadedBucketFile[];
  onFilesChange: (files: UploadedBucketFile[]) => void;
}) {
  const { t } = useTranslation();
  // One pending "describe then upload" slot per the Figma, with ADD MORE
  // appending another. A slot holds only a description until a file lands in
  // its own dropzone — at that point the pair is committed to the store and
  // the slot resets, ready for another. Local state because a slot with no
  // file yet isn't an uploaded document and has nothing to persist.
  // crypto.randomUUID() runs in the initializer, never during render.
  const [slots, setSlots] = useState<Array<{ uid: string; description: string }>>(() => [
    { uid: crypto.randomUUID(), description: '' },
  ]);
  const setSlotDescription = (uid: string, description: string) =>
    setSlots((prev) => prev.map((sl) => (sl.uid === uid ? { ...sl, description } : sl)));

  const acceptedAttr =
    allowedExtensions.length > 0 ? allowedExtensions.map((e) => `.${e}`).join(',') : undefined;
  const allowedListText = allowedExtensions.map((e) => e.toUpperCase()).join(', ');
  const maxBytes = maxFileSizeMb * 1024 * 1024;

  // Curried per slot so each dropzone commits its OWN description.
  const beforeUploadFor =
    (slotUid: string) =>
    (file: RcFile): boolean | string => {
      if (allowedExtensions.length > 0) {
        const dot = file.name.lastIndexOf('.');
        const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
        if (!allowedExtensions.includes(ext)) {
          void message.error(
            t('formRenderer.additionalDocuments.rejectedExtension', {
              name: file.name,
              allowed: allowedListText,
            }),
          );
          return Upload.LIST_IGNORE;
        }
      }
      if (file.size > maxBytes) {
        void message.error(
          t('formRenderer.additionalDocuments.rejectedSize', {
            name: file.name,
            max: maxFileSizeMb,
          }),
        );
        return Upload.LIST_IGNORE;
      }
      onFilesChange([
        ...files,
        {
          uid: `${file.name}-${file.size}-${file.lastModified}-${files.length}`,
          file,
          // Whatever the advisor typed beside THIS dropzone lands on the file.
          description: slots.find((sl) => sl.uid === slotUid)?.description.trim() ?? '',
        },
      ]);
      // Reset the slot so it's ready for another, rather than leaving the old
      // description sitting above an empty target.
      setSlotDescription(slotUid, '');
      return false;
    };

  const removeFile = (uid: string) => onFilesChange(files.filter((f) => f.uid !== uid));

  const setDescription = (uid: string, description: string) =>
    onFilesChange(files.map((f) => (f.uid === uid ? { ...f, description } : f)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Describe on the left, drop on the right — the order the Figma
          implies. Each slot's description belongs to whatever file lands in
          that slot's own dropzone; already-uploaded files keep their own
          editors in the list below, so nothing is edited from two places. */}
      {slots.map((slot) => (
        <div className="bmo-doc-slot" key={slot.uid}>
          <div>
            <Text style={{ fontSize: 13, color: '#475569' }}>
              {t('formRenderer.additionalDocuments.descriptionLabel')}
            </Text>
            <Input.TextArea
              rows={2}
              maxLength={999}
              showCount
              value={slot.description}
              onChange={(e) => setSlotDescription(slot.uid, e.target.value)}
              placeholder={t('formRenderer.additionalDocuments.descriptionPlaceholder')}
              style={{ marginTop: 4 }}
            />
          </div>
          {/* The left column has a "Description" label above its input, so the
              drop target needs the same leading offset to line up with the box
              rather than the label. A hidden copy of the label reserves exactly
              that height, which survives any font or spacing change. */}
          <div>
            <div className="bmo-doc-slot__label-spacer" aria-hidden="true">
              {t('formRenderer.additionalDocuments.descriptionLabel')}
            </div>
            <DropZone beforeUpload={beforeUploadFor(slot.uid)} acceptedAttr={acceptedAttr} t={t} />
          </div>
        </div>
      ))}

      <Button
        type="link"
        icon={<PlusCircleOutlined style={{ fontSize: 18 }} />}
        onClick={() => setSlots((prev) => [...prev, { uid: crypto.randomUUID(), description: '' }])}
        style={{
          alignSelf: 'flex-start',
          paddingLeft: 0,
          height: 'auto',
          color: '#0079C1',
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        {t('formRenderer.additionalDocuments.addMore')}
      </Button>

      {files.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {files.map((f) => (
            <li
              key={f.uid}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                // Extra bottom padding leaves room for AntD's showCount
                // counter (positioned absolutely just below the
                // TextArea) so `0 / 999` doesn't spill onto the row's
                // rounded border. Regular 10px top matches the entry
                // slot rows above.
                padding: '10px 12px 24px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {fileIcon(f.file.name)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {f.file.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {formatFileSize(f.file.size)}
                  </div>
                </div>
                <FileRowActions file={f.file} onRemove={() => removeFile(f.uid)} />
              </div>
              <TextArea
                value={f.description ?? ''}
                onChange={(e) => setDescription(f.uid, e.target.value)}
                placeholder={t('formRenderer.additionalDocuments.descriptionPlaceholder')}
                maxLength={999}
                showCount
                autoSize={{ minRows: 1, maxRows: 3 }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
